import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { VISA_STATUS } from '../constants';
import { type FileData } from '../../../components';

const GET_VISA_DETAILS = gql`
  query ($filter: VisaBaseFilter!) {
    visa(filter: $filter) {
      applicationId
      applicationNo
      referenceNo
      immdOfficerInCharge
      visaLabelApprovalDate
      applicationReceivedDate
      submissionDateToImmd
      status
      applicationDate
      visaDocument {
        id
        link
        name
      }
      entrySlipDocument {
        id
        link
        name
      }
    }
  }
`;

export type ApplicationVisaDetail = {
  applicationNo: string;
  applicationDate: string;
  referenceNo?: string;
  immdOfficerInCharge?: string;
  visaLabelApprovalDate?: Date;
  applicationReceivedDate?: Date;
  submissionDateToImmd?: Date;
  visaExpirationDate?: Date;
  status: VISA_STATUS;
  entrySlipDocument?: FileData;
  visaDocument?: FileData;
};

type GetApplicationVisaDetail = {
  applicationVisaDetail: ApplicationVisaDetail;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
};

export const useGetApplicationVisaDetail = (
  filter: any,
): GetApplicationVisaDetail => {
  const { data, loading, error, refetch } = useQuery(GET_VISA_DETAILS, {
    variables: { filter },
    fetchPolicy: 'network-only',
  });

  let applicationVisaDetail: ApplicationVisaDetail = {
    applicationNo: '',
    applicationDate: '',
    referenceNo: '',
    immdOfficerInCharge: '',
    visaLabelApprovalDate: undefined,
    applicationReceivedDate: undefined,
    submissionDateToImmd: undefined,
    status: VISA_STATUS.PENDING_APPLICATION_SUBMISSION,
  };

  if (!loading) {
    const {
      applicationNo,
      applicationDate,
      referenceNo,
      immdOfficerInCharge,
      visaLabelApprovalDate,
      applicationReceivedDate,
      submissionDateToImmd,
      visaExpirationDate,
      status,
      entrySlipDocument,
      visaDocument,
    } = data.visa ?? {};

    applicationVisaDetail = {
      applicationNo,
      applicationDate,
      referenceNo,
      immdOfficerInCharge: immdOfficerInCharge ?? undefined,
      visaLabelApprovalDate: visaLabelApprovalDate ?? undefined,
      applicationReceivedDate: applicationReceivedDate ?? undefined,
      submissionDateToImmd: submissionDateToImmd ?? undefined,
      visaExpirationDate: visaExpirationDate ?? undefined,
      status,
      entrySlipDocument: entrySlipDocument ?? undefined,
      visaDocument: visaDocument ?? undefined,
    };
  }

  return { applicationVisaDetail, loading, error, refetch };
};
